import React, { useState, useEffect, useRef } from "react";
import Account from "./Account";
import Patient from "./Patient";
import Insurance from "./Insurance";
import MoreDetails from "./MoreDetails";
import { Button } from "antd";
import { CheckOutlined, LoadingOutlined } from "@ant-design/icons";
import { Modal, message } from "antd";
import styles from "./Styles/NewForm.module.css";
import dayjs from "dayjs";
import { useAuth } from "../AuthProvider.js";
import APIService from "../../Service/APIService.js";
const steps = [
  { id: 1, label: "Account" },
  { id: 2, label: "Patient" },
  { id: 3, label: "Insurance" },
  { id: 4, label: "Details" },
];

const stepsDetails = [
  { label: "ACCOUNT DETAILS" },
  { label: "PATIENT DETAILS" },
  { label: "INSURANCE DETAILS" },
  { label: "MORE DETAILS & SIGNATURE" },
];

export default function NewForm() {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [openSectionId, setOpenSectionId] = useState(1);
  const [uploadedPatientFiles, setUploadedPatientFiles] = useState([]);
  const [formData, setFormData] = useState({
    submissionDate: dayjs().format("MM-DD-YYYY"),
    submittedBy: user?.FirstName + " " + user?.LastName || "First/Last Name",
    distributorName: "",
    distributorId: null,
    distributorUsername: "",
    distributorUserId: null,

    // Account details
    accountName: "",
    accountId: "",
    placeOfService: "",
    physicianName: "",
    contactName: "",
    addressLine1: "",
    addressLine2: "",
    cityStateZip: "",
    city: "",
    state: "",
    zip: "",
    phone: "",
    fax: "",
    medicareContractor: "",
    npi: "",
    tin: "",
    ptan: "",
    productGroupOptions: [],
    selectedProducts: [],

    // Patient details
    patientName: "",
    patientFirstName: "",
    patientLastName: "",
    patientDOB: "",
    patientPhone: "",
    patientEmail: "",
    patientAddress1: "",
    patientAddress2: "",
    patientCity: "",
    patientState: "",
    patientZip: "",
    patientOkayToContact: null,
    patientDemoSheetsFiles: [],

    // Insurance details
    primaryInsurance: "",
    policyNumber: "",
    primaryPlanType: "",
    subscriberNumber: "",
    subscriberDOB: "",
    insurancePhone: "",
    providerNetwork: "",
    patientInsuranceCardFiles: [],

    secondaryInsurance: "",
    secondaryPolicyNumber: "",
    secondaryPlanType: "",
    secondarySubscriberNumber: "",
    secondarySubscriberDOB: "",
    secondaryInsurancePhone: "",
    secondaryProviderNetwork: "",
    secondaryInsuranceCardFiles: [],

    // More details
    wounds: [],
    inSNF: "",
    surgicalGlobal: "",
    inHospice: "",
    selectedRegions: [],
    uploadedFiles: [],
    procedureDate: "",
    additionalDetails: "",
    signature: "",
    patientsClinicalRecord: [],

    unavailableProductsRequested: ""
  });

  const displayValue = (value) => {
    return value?.trim() ? (
      value
    ) : (
      <span style={{ color: "red", fontWeight: "500" }}>Empty</span>
    );
  };

  const normalizeProductOption = (option) => {
    if (!option) return null;
    if (typeof option === "string") {
      const trimmed = option.trim();
      return trimmed ? { label: trimmed, value: trimmed } : null;
    }

    const label =
      option.label ?? option.ProductGroupName ?? option.name ?? option.value ?? "";
    const value =
      option.value ?? option.ProductGroupID ?? option.id ?? option.label ?? "";

    if (label === "" || value === "") return null;
    return { label, value };
  };

  const hasSelectableProductOptions = (formData.productGroupOptions || [])
    .map(normalizeProductOption)
    .filter(Boolean).length > 0;

  const hasSelectedProducts = (formData.selectedProducts || [])
    .map(normalizeProductOption)
    .filter(Boolean).length > 0;

  const hasSelectedAccount = Boolean(
    String(formData.accountId ?? "").trim() &&
      String(formData.accountName ?? "").trim()
  );

  const getMoreDetailsValidationMessage = () => {
    if (hasSelectableProductOptions && !hasSelectedProducts) {
      return "Please select at least one product before submitting this IVR.";
    }
    if (!formData.signature?.trim()) {
      return "Please fill all required fields in More Details.";
    }
    return "";
  };

  const isMoreDetailsValid = () => getMoreDetailsValidationMessage() === "";

  const isStepValid = (stepId) => {
    switch (stepId) {
      case 1: // Account
        return (
          hasSelectedAccount &&
          formData.placeOfService?.trim() && formData.physicianName?.trim()
        );
      case 2: // Patient
        return (
          formData.patientFirstName?.trim() &&
          formData.patientLastName?.trim() &&
          formData.patientDOB?.trim()
        );
      case 3: // Insurance
        return formData.policyNumber?.trim();
      case 4: // More Details
        return isMoreDetailsValid();
      default:
        return false;
    }
  };

  useEffect(() => {
    setOpenSectionId(step);
  }, [step]);

  const handleToggleSection = (id) => {
    if (id === step || isStepValid(id)) {
      setOpenSectionId((prev) => (prev === id ? null : id));
    }
  };
  const handleNext = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\(\d{3}\)\s\d{3}-\d{4}$/;

    if (step === 2) {
      // PHONE VALIDATION
      if (formData.patientPhone!=="" && !phoneRegex.test(formData.patientPhone || "")) {
        message.error(
          "Please enter a valid phone number format: (123) 456-7890"
        );
        return;
      }

      // EMAIL VALIDATION
      if (formData.patientEmail && !emailRegex.test(formData.patientEmail)) {
        message.error("Please enter a valid email address");
        return;
      }
    }

    setStep(step + 1);
  };

  const handleBack = () => setStep((s) => Math.max(1, s - 1));
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [isSubmitInFlight, setIsSubmitInFlight] = useState(false);
  const submitInFlightRef = useRef(false);

  const handleIVRSubmit = async () => {
      if (submitInFlightRef.current) return;

      const validationMessage = getMoreDetailsValidationMessage();
      if (validationMessage) {
        message.error(validationMessage);
        return;
      }

      submitInFlightRef.current = true;
      setIsSubmitInFlight(true);
      console.log("Submitted:", formData);
      const serv = new APIService();
      try {
        const formDataObj = new FormData();
        // Append patientDemoSheetsFiles
        if (Array.isArray(formData.patientDemoSheetsFiles)) {
          formData.patientDemoSheetsFiles.forEach((file) => {
            formDataObj.append("patientDemoSheetsFiles", file);
          });
        }
        
        // Append patientInsuranceCardFiles
        if (Array.isArray(formData.patientInsuranceCardFiles)) {
          formData.patientInsuranceCardFiles.forEach((file) => {
            formDataObj.append("patientInsuranceCardFiles", file);
          });
        }
        
        // Append secondaryInsuranceCardFiles
        if (Array.isArray(formData.secondaryInsuranceCardFiles)) {
          formData.secondaryInsuranceCardFiles.forEach((file) => {
            formDataObj.append("secondaryInsuranceCardFiles", file);
          });
        }
        
        // Append patientsClinicalRecord
        if (Array.isArray(formData.patientsClinicalRecord)) {
          formData.patientsClinicalRecord.forEach((file) => {
            formDataObj.append("patientsClinicalRecord", file);
          });
        }
        
        formDataObj.append("data", JSON.stringify(formData));

        // const response = await serv.addDataToBackend(formData, "/ivr/submitnewivr");
        const response = await serv.addFileDataToBackend(
          formDataObj,
          "/ivr/submitnewivr",
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
        console.log("API Response:", response);
        message.success("IVR submitted successfully!")
        setIsSubmitModalOpen(false);
        window.location.href = "/IVRDetails/"+response?.data?.data?.ID; 
        // Optionally, reset form or redirect user here
      } catch (error) {
        console.error("API Error:", error);
        message.error(
          error?.response?.data?.message || "Failed to submit IVR. Please try again."
        );
      } finally {
        submitInFlightRef.current = false;
        setIsSubmitInFlight(false);
      }
  }

  return (
    <div className={styles.page}>
      {/* LEFT SIDE */}
      <div className={styles.left}>
        <div className={styles.header}>
          <p className={styles.headTitle}>IVRs</p>
          <h1 className={styles.pageTitle}>New IVR Form</h1>
        </div>

        {/* Stepper */}
        <div className={styles.stepper}>
          {steps.map((st, index) => (
            <React.Fragment key={st.id}>
              <button
                className={`${styles.step} ${step === st.id ? styles.activeStep : ""
                  }`}
                onClick={() => {
                  if (st.id === step || isStepValid(st.id)) {
                    setStep(st.id);
                  }
                }}
              >
                <span
                  className={`${styles.stepNumber} ${st.id < step ? styles.completedStep : ""
                    } ${step === st.id ? styles.activeStepNumber : ""}`}
                >
                  {st.id}
                </span>
                <span className={styles.stepLabel}>{st.label}</span>
              </button>

              {index < steps.length - 1 && (
                <div
                  className={`${styles.dotted} ${st.id < step ? styles.completedDotted : ""
                    }`}
                ></div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* STEP CONTENT */}
        <div className={styles.sectionContainer}>
          {step === 1 && (
            <Account formData={formData} setFormData={setFormData} />
          )}
          {step === 2 && (
            <Patient
              formData={formData}
              setFormData={setFormData}
            />
          )}
          {step === 3 && (
            <Insurance formData={formData} setFormData={setFormData} />
          )}

          {step === 4 && (
            <MoreDetails formData={formData} setFormData={setFormData} />
          )}

          <div
            className={styles.buttonRow}
            style={{
              justifyContent: step === 1 ? "flex-end" : "space-between",
            }}
          >
            {step > 1 && (
              <button className={styles.backBtn} onClick={handleBack}>
                <span className={styles.buttonText}>← Back</span>
              </button>
            )}

            {step < 4 && (
              <button
                className={`${styles.nextBtn} ${!isStepValid(step) ? styles.buttonDisabled : ""
                  }`}
                onClick={handleNext}
                disabled={!isStepValid(step)}
              >
                <span className={styles.buttonText}>Next →</span>
              </button>
            )}

            {step === 4 && (
              <Button
                onClick={() => {
                  const validationMessage = getMoreDetailsValidationMessage();
                  if (!validationMessage) {
                    setIsSubmitModalOpen(true);
                  } else {
                    message.error(validationMessage);
                  }
                }}
                type="primary"
                icon={<CheckOutlined />}
                style={{
                  background: "#27a907",
                  fontSize: "14px",
                  fontWeight: "600",
                }}
              >
                Submit
              </Button>
            )}
            <Modal
              open={isSubmitModalOpen}
              footer={null}
              closable={false}
              maskClosable={!isSubmitInFlight}
              keyboard={!isSubmitInFlight}
              onCancel={() => {
                if (!isSubmitInFlight) {
                  setIsSubmitModalOpen(false);
                }
              }}
              centered
              className={styles.submitModal}
            >
              <div className={styles.submitHeader}>
                <span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="32"
                    height="32"
                    viewBox="0 0 32 32"
                    fill="none"
                  >
                    <path
                      d="M5.3335 7.99935V25.3327C5.3335 27.5419 7.12436 29.3327 9.3335 29.3327H22.6668C24.876 29.3327 26.6668 27.5419 26.6668 25.3327V11.9993C26.6668 9.79022 24.876 7.99935 22.6668 7.99935H5.3335ZM5.3335 7.99935V6.66602"
                      stroke="#303030"
                      stroke-width="2"
                    />
                    <path
                      d="M24.0002 7.99826V8.99826H25.0002V7.99826H24.0002ZM6.76159 8.99826H24.0002V6.99826H6.76159V8.99826ZM25.0002 7.99826V5.73962H23.0002V7.99826H25.0002ZM20.815 2.10981L6.4182 4.16649L6.70104 6.14638L21.0978 4.0897L20.815 2.10981ZM6.4182 4.16649C5.222 4.33737 4.3335 5.36183 4.3335 6.57017H6.3335C6.3335 6.35713 6.49015 6.17651 6.70104 6.14638L6.4182 4.16649ZM25.0002 5.73962C25.0002 3.50827 23.0239 1.79425 20.815 2.10981L21.0978 4.0897C22.1019 3.94627 23.0002 4.72537 23.0002 5.73962H25.0002ZM6.76159 6.99826C6.52516 6.99826 6.3335 6.80659 6.3335 6.57017H4.3335C4.3335 7.91117 5.42059 8.99826 6.76159 8.99826V6.99826Z"
                      fill="#303030"
                    />
                    <path
                      d="M10.6665 16H21.3332"
                      stroke="#303030"
                      stroke-width="2"
                      stroke-linecap="round"
                    />
                    <path
                      d="M10.6665 20.666H17.9998"
                      stroke="#303030"
                      stroke-width="2"
                      stroke-linecap="round"
                    />
                  </svg>
                </span>{" "}
                <h1 className={styles.modalTitle}>Submit IVR</h1>
              </div>

              <div className={styles.modalFrame}>
                <p className={styles.modalText}>
                  Please allow <strong>24 to 48 hours</strong> for the
                  processing of IVR submissions. Results will be uploaded to the
                  account as soon as they become available.
                </p>
                <div className={styles.modalNote}>
                  {" "}
                  <p className={styles.modalNoteTitle}>
                    <strong>Please Note:</strong>
                  </p>
                  <ul className={styles.modalList}>
                    <li>
                      IVRs submitted after <strong>4:30 PM EST</strong> will be
                      processed on the next business day.
                    </li>
                    <li>
                      If a prior authorization is required or if we are
                      processing an Advantage Plan, additional time may be
                      needed.
                    </li>
                  </ul>
                </div>

                <p className={styles.modalFooterText}>
                  We appreciate your patience and understanding.
                </p>
              </div>

              <div className={styles.modalButtons}>
                <button
                  className={styles.cancelBtn}
                  onClick={() => setIsSubmitModalOpen(false)}
                  disabled={isSubmitInFlight}
                >
                  Cancel
                </button>

                <button
                  className={styles.confirmBtn}
                  onClick={handleIVRSubmit}
                  disabled={isSubmitInFlight}
                >
                  {isSubmitInFlight ? (
                    <>
                      <LoadingOutlined spin style={{ marginRight: 8 }} />
                      Submitting...
                    </>
                  ) : (
                    "Confirm"
                  )}
                </button>
              </div>
            </Modal>
          </div>
        </div>
      </div>

      {/* RIGHT SIDE */}
      <div className={styles.right}>
        <div className={styles.rightTitle}>
          <h2 className={styles.formDetails}>Form Details</h2>
          <p className={styles.accountNameHeader}>
            {displayValue(formData.accountName)}
          </p>
        </div>

        <div className={styles.detailsContainer}>
          <div className={styles.submissionDetails}>
            <div className={styles.detailRow}>
              <p className={styles.detailLabel}>Submission Date</p>
              <p className={styles.detailValue}>
                {displayValue(formData.submissionDate)}
              </p>
            </div>
            <div className={styles.detailRow}>
              <p className={styles.detailLabel}>Submitted By</p>
              <p className={styles.detailValue}>
                {displayValue(formData.submittedBy)}
              </p>
            </div>
            <div className={styles.detailRow}>
              <p className={styles.detailLabel}>Distributor</p>
              <p className={styles.detailValue}>
                {displayValue(formData.distributorName)}
              </p>
            </div>
            {/* <div className={styles.detailRow}>
              <p className={styles.detailLabel}>Distributor User</p>
              <p className={styles.detailValue}>
                {displayValue(formData.distributorUser)}
              </p>
            </div> */}
          </div>

          <hr className={styles.line} />

          {[1, 2, 3, 4].map((id) => (
            <div key={id}>
              <div
                className={styles.block}
                onClick={() => handleToggleSection(id)}
              >
                <h3 className={styles.blockTitle}>
                  {stepsDetails[id - 1].label}
                </h3>
                <span>
                  {openSectionId === id ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      className={styles.chevronup}
                    >
                      <path
                        d="M13.5556 11.334L8.00001 5.77843L2.44446 11.334"
                        stroke="#303030"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <path
                        d="M13.5556 5.77734L8.00001 11.3329L2.44446 5.77734"
                        stroke="#303030"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </span>
              </div>

              {openSectionId === id && (
                <div className={styles.detailsContent}>
                  {id === 1 && (
                    <>
                      <div className={styles.detailRow}>
                        <p className={styles.detailLabel}>Place of Service</p>
                        <p className={styles.detailValue}>
                          {displayValue(formData.placeOfService)}
                        </p>
                      </div>
                      <div className={styles.detailRow}>
                        <p className={styles.detailLabel}>Physician’s Name</p>
                        <p className={styles.detailValue}>
                          {displayValue(formData.physicianName)}
                        </p>
                      </div>
                      {/* <div className={styles.detailRow}>
                        <p className={styles.detailLabel}>Contact Name</p>
                        <p className={styles.detailValue}>
                          {displayValue(formData.contactName)}
                        </p>
                      </div> */}
                      <div className={styles.detailRow}>
                        <p className={styles.detailLabel}>Address</p>
                        <p className={styles.detailValueMultiLine}>
                          {displayValue(formData.addressLine1)}

                          <br />
                          {displayValue(formData.addressLine2)}
                          <br />
                          {displayValue(formData.city + ", " + formData.state + " " + formData.zip)}
                        </p>
                      </div>
                      <div className={styles.detailRow}>
                        <p className={styles.detailLabel}>Phone</p>
                        <p className={styles.detailValue}>
                          {displayValue(formData.phone)}
                        </p>
                      </div>
                      <div className={styles.detailRow}>
                        <p className={styles.detailLabel}>Fax</p>
                        <p className={styles.detailValue}>
                          {displayValue(formData.fax)}
                        </p>
                      </div>
                      <div className={styles.detailRow}>
                        <p className={styles.detailLabel}>
                          Medicare Admin Contractor
                        </p>
                        <p className={styles.detailValue}>
                          {displayValue(formData.medicareContractor)}{" "}
                        </p>
                      </div>
                      <div className={styles.detailRow}>
                        <p className={styles.detailLabel}>NPI</p>
                        <p className={styles.detailValue}>
                          {displayValue(formData.npi)}
                        </p>
                      </div>
                      <div className={styles.detailRow}>
                        <p className={styles.detailLabel}>TIN</p>
                        <p className={styles.detailValue}>
                          {displayValue(formData.tin)}
                        </p>
                      </div>
                      <div className={styles.detailRow}>
                        <p className={styles.detailLabel}>PTAN</p>
                        <p className={styles.detailValue}>
                          {displayValue(formData.ptan)}
                        </p>
                      </div>
                    </>
                  )}
                  {id === 2 && (
                    <>
                      <div className={styles.detailRow}>
                        <p className={styles.detailLabel}>Patient Name</p>
                        <p className={styles.detailValue}>
                          {formData.patientFirstName || formData.patientLastName
                            ? `${formData.patientFirstName || ""} ${formData.patientLastName || ""
                              }`.trim()
                            : "Patient Name"}
                        </p>
                      </div>

                      <div className={styles.detailRow}>
                        <p className={styles.detailLabel}>Date of Birth</p>
                        <p className={styles.detailValue}>
                          {displayValue(formData.patientDOB)}
                        </p>
                      </div>

                      <div className={styles.detailRow}>
                        <p className={styles.detailLabel}>Phone</p>
                        <p className={styles.detailValue}>
                          {displayValue(formData.patientPhone)}
                        </p>
                      </div>

                      <div className={styles.detailRow}>
                        <p className={styles.detailLabel}>Email</p>
                        <p className={styles.detailValue}>
                          {displayValue(formData.patientEmail)}
                        </p>
                      </div>

                      <div className={styles.detailRow}>
                        <p className={styles.detailLabel}>Address</p>
                        <p className={styles.detailValueMultiLine}>
                          {formData.patientAddress1 ||
                            formData.patientAddress2 ||
                            formData.patientCity ||
                            formData.patientState ||
                            formData.patientZip ? (
                            <>
                              {formData.patientAddress1 && (
                                <>
                                  {formData.patientAddress1}
                                  <br />
                                </>
                              )}
                              {formData.patientAddress2 && (
                                <>
                                  {formData.patientAddress2}
                                  <br />
                                </>
                              )}
                              {(formData.patientCity ||
                                formData.patientState ||
                                formData.patientZip) && (
                                  <>
                                    {formData.patientCity}
                                    {formData.patientCity &&
                                      (formData.patientState || formData.patientZip)
                                      ? ", "
                                      : ""}
                                    {formData.patientState} {formData.patientZip}
                                  </>
                                )}
                            </>
                          ) : (
                            <span style={{ color: "red", fontWeight: "500" }}>
                              Empty
                            </span>
                          )}
                        </p>
                      </div>

                      <div className={styles.detailRow}>
                        <p className={styles.detailLabel}>
                          Okay to Contact Patient
                        </p>
                        <p className={styles.detailValue}>
                          {formData.patientOkayToContact === "yes" ? (
                            "Yes"
                          ) : formData.patientOkayToContact === "no" ? (
                            "No"
                          ) : (
                            <span style={{ color: "red", fontWeight: "500" }}>
                              Empty
                            </span>
                          )}
                        </p>
                      </div>

                      <div className={styles.detailRow}>
                        <p className={styles.detailLabel}>
                          Patient Demo Sheets
                        </p>
                        <p className={styles.detailValue}>
                          {uploadedPatientFiles.length > 0 ? (
                            `${uploadedPatientFiles.length} file${uploadedPatientFiles.length > 1 ? "s" : ""
                            } uploaded`
                          ) : (
                            <span style={{ color: "red", fontWeight: "500" }}>
                              Empty
                            </span>
                          )}
                        </p>
                      </div>
                    </>
                  )}
                  {id === 3 && (
                    <>
                      {/* --- PRIMARY INSURANCE --- */}
                      <p className={styles.sectionTitle}>Primary Insurance</p>

                      <div className={styles.detailRow}>
                        <p className={styles.detailLabel}>Insurance Company</p>
                        <p className={styles.detailValue}>
                          {displayValue(formData.primaryInsurance)}
                        </p>
                      </div>

                      <div className={styles.detailRow}>
                        <p className={styles.detailLabel}>Policy Number</p>
                        <p className={styles.detailValue}>
                          {displayValue(formData.policyNumber)}
                        </p>
                      </div>

                      <div className={styles.detailRow}>
                        <p className={styles.detailLabel}>Type of Plan</p>
                        <p className={styles.detailValue}>
                          {displayValue(formData.primaryPlanType)}
                        </p>
                      </div>

                      <div className={styles.detailRow}>
                        <p className={styles.detailLabel}>Subscriber Number</p>
                        <p className={styles.detailValue}>
                          {displayValue(formData.subscriberNumber)}
                        </p>
                      </div>

                      <div className={styles.detailRow}>
                        <p className={styles.detailLabel}>Subscriber DOB</p>
                        <p className={styles.detailValue}>
                          {displayValue(formData.subscriberDOB)}
                        </p>
                      </div>

                      <div className={styles.detailRow}>
                        <p className={styles.detailLabel}>Insurance Phone</p>
                        <p className={styles.detailValue}>
                          {displayValue(formData.insurancePhone)}
                        </p>
                      </div>

                      <div className={styles.detailRow}>
                        <p className={styles.detailLabel}>
                          Provider in Network
                        </p>
                        <p className={styles.detailValue}>
                          {displayValue(formData.providerNetwork)}
                        </p>
                      </div>

                      {/* --- SECONDARY INSURANCE --- */}
                      {formData.secondaryInsurance && (
                        <>
                          <p className={styles.sectionTitle}>
                            Secondary Insurance
                          </p>

                          <div className={styles.detailRow}>
                            <p className={styles.detailLabel}>
                              Insurance Company
                            </p>
                            <p className={styles.detailValue}>
                              {displayValue(formData.secondaryInsurance)}
                            </p>
                          </div>

                          <div className={styles.detailRow}>
                            <p className={styles.detailLabel}>Policy Number</p>
                            <p className={styles.detailValue}>
                              {displayValue(formData.secondaryPolicyNumber)}
                            </p>
                          </div>

                          <div className={styles.detailRow}>
                            <p className={styles.detailLabel}>Type of Plan</p>
                            <p className={styles.detailValue}>
                              {displayValue(formData.secondaryPlanType)}
                            </p>
                          </div>

                          {formData.secondarySubscriberNumber && (
                            <div className={styles.detailRow}>
                              <p className={styles.detailLabel}>
                                Subscriber Number
                              </p>
                              <p className={styles.detailValue}>
                                {displayValue(
                                  formData.secondarySubscriberNumber
                                )}
                              </p>
                            </div>
                          )}

                          {formData.secondarySubscriberDOB && (
                            <div className={styles.detailRow}>
                              <p className={styles.detailLabel}>
                                Subscriber DOB
                              </p>
                              <p className={styles.detailValue}>
                                {displayValue(formData.secondarySubscriberDOB)}
                              </p>
                            </div>
                          )}

                          {/* --- Optional fields ---*/}
                          {formData.secondaryInsurancePhone && (
                            <div className={styles.detailRow}>
                              <p className={styles.detailLabel}>
                                Insurance Phone
                              </p>
                              <p className={styles.detailValue}>
                                {displayValue(formData.secondaryInsurancePhone)}
                              </p>
                            </div>
                          )}

                          {formData.secondaryProviderNetwork && (
                            <div className={styles.detailRow}>
                              <p className={styles.detailLabel}>
                                Provider in Network
                              </p>
                              <p className={styles.detailValue}>
                                {displayValue(
                                  formData.secondaryProviderNetwork
                                )}
                              </p>
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}

                  {id === 4 && (
                    <>
                      {/* --- Number of Wounds --- */}
                      <div className={styles.detailRow}>
                        <p className={styles.detailLabel}>Number of Wounds</p>
                        <p className={styles.detailValue}>
                          {formData.wounds &&
                            formData.wounds.filter((w) => w.saved).length > 0 ? (
                            // formData.wounds.filter((w) => w.saved).length
                            formData.wounds.length
                          ) : (
                            <span style={{ color: "red", fontWeight: "500" }}>
                              Empty
                            </span>
                          )}
                        </p>
                      </div>

                      {/* --- Procedure Date --- */}
                      <div className={styles.detailRow}>
                        <p className={styles.detailLabel}>Procedure Date</p>
                        <p className={styles.detailValue}>
                          {displayValue(formData.procedureDate)}
                        </p>
                      </div>

                      {/* --- In SNF --- */}
                      <div className={styles.detailRow}>
                        <p className={styles.detailLabel}>In SNF</p>
                        <p className={styles.detailValue}>
                          {displayValue(formData.inSNF)}
                        </p>
                      </div>

                      {/* --- Surgical Global Period --- */}
                      <div className={styles.detailRow}>
                        <p className={styles.detailLabel}>
                          Surgical Global Period
                        </p>
                        <p className={styles.detailValue}>
                          {displayValue(formData.surgicalGlobal)}
                        </p>
                      </div>

                      {/* --- In Hospice --- */}
                      <div className={styles.detailRow}>
                        <p className={styles.detailLabel}>In Hospice</p>
                        <p className={styles.detailValue}>
                          {displayValue(formData.inHospice)}
                        </p>
                      </div>

                      {/* --- Number of Selected Products --- */}
                      <div className={styles.detailRow}>
                        <p className={styles.detailLabel}>Selected Products</p>
                        {formData.selectedProducts &&
                          formData.selectedProducts.length > 0 ? (
                          <ul className={styles.fileList}>
                            {formData.selectedProducts.map((product, idx) => {
                              const text =
                                typeof product === "string"
                                  ? product
                                  : product?.label || product?.value || "";
                              return <li key={idx}>{text}</li>;
                            })}
                          </ul>
                        ) : (
                          <span style={{ color: "red", fontWeight: "500" }}>
                            Empty
                          </span>
                        )}
                      </div>

                      {/* --- Patient’s Clinical Record --- */}
                      <div className={styles.detailRow}>
                        <p className={styles.detailLabel}>
                          Patient's Clinical Record
                        </p>
                        {formData.uploadedFiles &&
                          formData.uploadedFiles.length > 0 ? (
                          <ul className={styles.fileList}>
                            {formData.uploadedFiles.map((file, idx) => (
                              <li key={idx}>{file.name}</li>
                            ))}
                          </ul>
                        ) : (
                          <span style={{ color: "red", fontWeight: "500" }}>
                            Empty
                          </span>
                        )}
                      </div>

                      {/* --- Signature --- */}
                      <div className={styles.detailRow}>
                        <p className={styles.detailLabel}>Signature</p>
                        <p className={styles.detailValue}>
                          {displayValue(formData.signature)}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              )}

              {id !== 4 && <hr className={styles.line} />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
